interface FigureBinding {
  readonly figure: HTMLElement;
  readonly click: (event: Event) => void;
  readonly keydown: (event: KeyboardEvent) => void;
  readonly addedTabIndex: boolean;
  readonly addedRole: boolean;
  readonly addedHasPopup: boolean;
  readonly addedLabel: boolean;
}

export class FigureExpander {
  #selector: string;
  #bindings: FigureBinding[] = [];
  #currentExpandedFigure: HTMLDialogElement | null = null;
  #currentTrigger: HTMLElement | null = null;

  constructor(options: { selector: string }) {
    this.#selector = options.selector;
    this.init();
  }

  init() {
    const figures = document.querySelectorAll<HTMLElement>(this.#selector);
    figures.forEach((figure) => {
      if (this.#bindings.some((binding) => binding.figure === figure)) return;
      if (!this.#expandableChild(figure)) return;

      const click = (event: Event) => this.expandElement(event);
      const keydown = (event: KeyboardEvent) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        this.expandElement(event);
      };
      const addedTabIndex = !figure.hasAttribute("tabindex");
      const addedRole = !figure.hasAttribute("role");
      const addedHasPopup = !figure.hasAttribute("aria-haspopup");
      const addedLabel = !figure.hasAttribute("aria-label");

      figure.addEventListener("click", click);
      figure.addEventListener("keydown", keydown);
      figure.classList.add("expandable-figure");
      if (addedTabIndex) figure.setAttribute("tabindex", "0");
      if (addedRole) figure.setAttribute("role", "button");
      if (addedHasPopup) figure.setAttribute("aria-haspopup", "dialog");
      if (addedLabel) figure.setAttribute("aria-label", this.#triggerLabel(figure));

      this.#bindings.push({
        figure,
        click,
        keydown,
        addedTabIndex,
        addedRole,
        addedHasPopup,
        addedLabel,
      });
    });
  }

  expandElement(event: Event) {
    const figure = event.currentTarget as HTMLElement;
    const child = this.#expandableChild(figure);
    if (!child) return;

    this.#currentExpandedFigure?.close();

    const expanded = document.createElement("figure");
    expanded.className = "expanded-figure";
    expanded.appendChild(child.cloneNode(true));

    const dialog = document.createElement("dialog");
    dialog.className = "expandable-figure-dialog";
    dialog.setAttribute("aria-label", figure.getAttribute("aria-label") ?? "Expanded figure");

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "expandable-figure-close-button";
    closeButton.textContent = "×";
    closeButton.setAttribute("aria-label", "Close expanded figure");
    closeButton.addEventListener("click", () => dialog.close());

    dialog.appendChild(closeButton);
    dialog.appendChild(expanded);
    document.body.appendChild(dialog);

    this.#currentExpandedFigure = dialog;
    this.#currentTrigger = figure;

    dialog.addEventListener("click", (dialogEvent) => {
      if (dialogEvent.target === dialog) dialog.close();
    });

    dialog.addEventListener("close", () => {
      dialog.remove();
      if (this.#currentExpandedFigure === dialog) this.#currentExpandedFigure = null;
      const trigger = this.#currentTrigger;
      this.#currentTrigger = null;
      if (trigger?.isConnected) trigger.focus();
    });

    dialog.showModal();
    closeButton.focus();
  }

  destroy() {
    this.#currentExpandedFigure?.close();
    for (const binding of this.#bindings) {
      const { figure } = binding;
      figure.removeEventListener("click", binding.click);
      figure.removeEventListener("keydown", binding.keydown);
      figure.classList.remove("expandable-figure");
      if (binding.addedTabIndex) figure.removeAttribute("tabindex");
      if (binding.addedRole) figure.removeAttribute("role");
      if (binding.addedHasPopup) figure.removeAttribute("aria-haspopup");
      if (binding.addedLabel) figure.removeAttribute("aria-label");
    }
    this.#bindings = [];
  }

  #expandableChild(figure: HTMLElement): Element | null {
    return figure.querySelector("img") ?? figure.querySelector("svg") ?? figure.firstElementChild;
  }

  #triggerLabel(figure: HTMLElement): string {
    const imageAlt = figure.querySelector("img")?.getAttribute("alt")?.trim();
    return imageAlt ? `Expand ${imageAlt}` : "Expand figure";
  }
}
